namespace $ {

	function $mol_wire_task_number( name: string, fallback: number ) {
		const global_value = ( globalThis as any )[ name ]
		if( Number.isFinite( global_value ) ) return Number( global_value )

		try {
			const local = globalThis.localStorage?.getItem( name )
			if( local !== null && Number.isFinite( Number( local ) ) ) return Number( local )
		} catch {}

		try {
			const session = globalThis.sessionStorage?.getItem( name )
			if( session !== null && Number.isFinite( Number( session ) ) ) return Number( session )
		} catch {}

		return fallback
	}

	function $mol_wire_task_arg_type( arg: unknown ) {
		if( arg === null ) return 'null'
		if( arg === undefined ) return 'undefined'
		if( typeof arg !== 'object' ) return typeof arg
		return Object.prototype.toString.call( arg ).replace( /^\[object |\]$/g, '' )
	}

	function $mol_wire_task_arg_sample( arg: unknown ) {
		if( arg === null ) return 'null'
		if( arg === undefined ) return 'undefined'
		if( typeof arg === 'string' ) return JSON.stringify( arg.length > 80 ? arg.slice( 0, 77 ) + '...' : arg )
		if( typeof arg !== 'object' ) return String( arg )

		const obj = arg as any
		const type = $mol_wire_task_arg_type( arg )
		const size = Number.isFinite( obj.size ) ? ` size=${ obj.size }` : ''
		const mime = typeof obj.type === 'string' && obj.type ? ` type=${ JSON.stringify( obj.type ) }` : ''
		const tag = obj[ Symbol.toStringTag ] ? ` tag=${ String( obj[ Symbol.toStringTag ] ) }` : ''
		return `${ type }${ size }${ mime }${ tag }`
	}

	function $mol_wire_task_has_unstable_object_arg( args: readonly unknown[] ) {
		return args.some( arg => {
			if( arg === null || typeof arg !== 'object' ) return false
			return [
				'Blob',
				'File',
				'Response',
				'Request',
				'FormData',
				'ReadableStream',
			].includes( $mol_wire_task_arg_type( arg ) )
		} )
	}

	function $mol_wire_task_unstable_args_error(
		sub: $mol_wire_sub | null,
		prev: $mol_wire_task< any, any, any >,
		next: $mol_wire_task< any, any, any >,
	) {
		return new Error( [
			'$mol_wire possible non-idempotent async dependency',
			`parent fiber: ${ String( sub ?? '' ) }`,
			`prev child fiber: ${ prev }`,
			`next child fiber: ${ next }`,
			`child task: ${ next.task?.name || '(anonymous)' }`,
			`child host: ${ String( next.host ?? '' ) }`,
			`prev args: ${ prev.args.map( $mol_wire_task_arg_sample ).join( ', ' ) }`,
			`next args: ${ next.args.map( $mol_wire_task_arg_sample ).join( ', ' ) }`,
			'hint: A parent fiber restarted with a fresh async object argument. Read the source through a stable host first, for example $mol_wire_sync(fileEntity).blob(), then pass the stabilized result.',
		].join( '\n' ) )
	}
	
	/** One-shot fiber */
	export class $mol_wire_task<
		Host,
		Args extends readonly unknown[],
		Result,
	> extends $mol_wire_fiber< Host, Args, Result > {
		
		static getter<
			Host,
			Args extends readonly unknown[],
			Result,
		>(
			task: ( this : Host , ... args : Args )=> Result,
		): ( host: Host, args: Args )=> $mol_wire_task< Host, Args, Result > {
			
			return function $mol_wire_task_get( host: Host, args: Args ) {
				
				const sub = $mol_wire_auto()
				const existen = sub?.track_next() as $mol_wire_task< Host, Args, Result > | undefined
				let cause = ''
				
				reuse: if( existen ) {
					
					if( !existen.temp ) break reuse
					
					if( existen.task !== task ) { cause = 'task'; break reuse }
					if( existen.host !== host ) { cause = 'host'; break reuse }
					if( !$mol_compare_deep( existen.args, args ) ) { cause = 'args'; break reuse }
					
					return existen
				}
				
				const key = ( (host as any)?.[ Symbol.toStringTag ] ?? host ) + ( '.' + task.name + '<#>' )
				const next = new $mol_wire_task( key, task, host, args )
				
				// Disabled because non-idempotency is required for try-catch
				if( existen?.temp ) {
					if(
						cause === 'args'
						&& $mol_wire_task_number( 'wire_patch_unstable_child_error', 1 )
						&& (
							$mol_wire_task_has_unstable_object_arg( existen.args )
							|| $mol_wire_task_has_unstable_object_arg( args )
						)
					) {
						throw $mol_wire_task_unstable_args_error( sub, existen, next )
					}

					$$.$mol_log3_warn({
						place: '$mol_wire_task',
						message: `Different ${cause} on restart`,
						sub,
						prev: existen,
						next,
						hint: 'Maybe required additional memoization',
					})
				}
				
				return next
			}
			
		}

		get temp() {
			return true
		}
		
		complete() {
			if( $mol_promise_like( this.cache ) ) return
			this.destructor()
		}
		
		put( next: Result | Error | Promise< Result | Error > ) {
			
			const prev = this.cache
			this.cache = next
			
			if( $mol_promise_like( next ) ) {
				
				this.cursor = $mol_wire_cursor.fresh
				if( next !== prev ) this.emit()
				
				if( $mol_owning_catch( this, next ) ) {
					try {
						(next as any)[ Symbol.toStringTag ] = (this as any)[ Symbol.toStringTag ]
					} catch { // Promises throw in strict mode
						Object.defineProperty( next, Symbol.toStringTag, { value: (this as any)[ Symbol.toStringTag ] } )
					}
				}

				return next
			}
			
			this.cursor = $mol_wire_cursor.final
			
			if( this.sub_empty ) this.destructor()
			else if( next !== prev ) this.emit()
			
			return next
		}
		
		destructor() {
			super.destructor()
			this.cursor = $mol_wire_cursor.final
		}
		
	}
	
}
